import { sessionStore, localStore } from "@auvious/utils";
import {
  KEY_AGENT_CONNECTED,
  KEY_CONVERSATION_REQUEST,
  KEY_CONVERSATION_TYPE,
  KEY_GREETED,
  KEY_TIME_IN_QUEUE,
  KEY_VIDEO_POPOUT,
} from "../models";
import { ConversationFactory, Restorable } from "@auvious/integrations";
import { ConversationAuthService } from "./conversation.auth.service";
import { Log } from "./log.service";
import * as LZString from "lz-string";
import { CobrowseService } from "./cobrowse.service";
import { CallService } from "./call.service";

class StorageServiceCtrl extends Restorable {
  storage: Map<string, string> = new Map();

  constructor() {
    super(
      [
        KEY_VIDEO_POPOUT,
        KEY_TIME_IN_QUEUE,
        KEY_AGENT_CONNECTED,
        KEY_CONVERSATION_REQUEST,
        KEY_GREETED,
      ],
      [KEY_CONVERSATION_TYPE]
    );
  }

  setItem(key, value, storage: "session" | "local" = "local") {
    try {
      if (storage === "local") {
        localStore.setItem(key, value);
      }
      if (storage === "session") {
        sessionStore.setItem(key, value);
      }
    } catch (ex) {
      this.storage.set(key, value);
    }
  }

  getItem(key, storage: "session" | "local" = "local"): string {
    try {
      if (storage === "local") {
        return localStore.getItem(key);
      }
      if (storage === "session") {
        return sessionStore.getItem(key);
      }
    } catch (ex) {
      return this.storage.get(key);
    }
  }

  removeItem(key, storage: "session" | "local" = "local") {
    try {
      if (storage === "local") {
        localStore.removeItem(key);
      }
      if (storage === "session") {
        sessionStore.removeItem(key);
      }
    } catch (ex) {
      this.storage.delete(key);
    }
  }

  private getUsedTickets(key: string): string[] {
    let usedTickets = [];
    try {
      const ticketsStr = StorageService.getItem(key, "session");
      usedTickets = !!ticketsStr ? JSON.parse(ticketsStr) : [];
    } catch (ex) {
      // do nothing
    }
    return usedTickets;
  }

  public setUsedTicket(key: string, ticket: string): void {
    const usedTickets = this.getUsedTickets(key);
    usedTickets.push(ticket);
    StorageService.setItem(key, JSON.stringify(usedTickets), "session");
  }

  public clearUsedTickets(key: string): void {
    StorageService.removeItem(key, "session");
  }

  public isTicketUsed(key: string, ticket: string): boolean {
    const usedTickets = this.getUsedTickets(key);
    return usedTickets.includes(ticket);
  }

  /** Accepts an object with the state to be restored */
  public restoreState(state: object) {
    Log.info("Restoring state", state);
    super.restoreState(state);
    try {
      ConversationFactory.instance?.restoreState(state);
    } catch (ex) {}
    try {
      ConversationAuthService.client?.restoreState(state);
    } catch (ex) {}
    try {
      CobrowseService.restoreState(state);
    } catch (ex) {}
    try {
      CallService.restoreState(state);
    } catch (ex) {}
  }

  /** Returns an object with all the keys that need to be restored */
  public getRestorableState() {
    // other services
    let state = {
      ...super.getRestorableState(),
      ...CobrowseService.getRestorableState(),
      ...CallService.getRestorableState(),
    };
    try {
      state = {
        ...state,
        ...ConversationFactory.instance?.getRestorableState(),
      };
    } catch (ex) {}
    try {
      state = {
        ...state,
        ...ConversationAuthService.client?.getRestorableState(),
      };
    } catch (ex) {}

    return state;
  }

  /** Returns a base64 string of the state to be restored */
  public async getRestorableStateToString(): Promise<string> {
    const state = this.getRestorableState();
    return Object.keys(state).length > 0
      ? await this.compress(JSON.stringify(state))
      : undefined;
  }

  /** Accepts a base64 string that converts to object */
  public async restoreStateFromString(string: string) {
    try {
      const stateStr = await this.decompress(string);
      if (stateStr) {
        const state = JSON.parse(stateStr);
        this.restoreState(state);
      }
    } catch (ex) {
      Log.error(ex);
    }
  }

  compress(str): string {
    return LZString.compressToEncodedURIComponent(str);
  }

  decompress(str): string {
    return LZString.decompressFromEncodedURIComponent(str);
  }

  get isDebugOn() {
    try {
      return !!localStore.getItem("debug");
    } catch (ex) {
      return false;
    }
  }
}

export const StorageService = new StorageServiceCtrl();
